/**
 * Service frontend pour la charte graphique du space (chantier L 2026-05).
 *
 * Backend correspondant : `/raiso-back-end/app/routes/brand_config.py`.
 *
 * Fix backlog 3.2 (19-05) : cache en mémoire avec TTL 5 min pour éviter le
 * spinner de 6s à chaque switch vers le toggle Charte graphique. Le cache est
 * invalidé automatiquement après `update` ou `extract` (cf. méthodes).
 */
import { apiClient } from './apiClient';
import { Logger } from '../utils/logger';
import type { BrandConfig } from '../types/brandConfig';

// Cache module-level (vit pour toute la session)
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const cache = new Map<string, { data: BrandConfig | null; expiresAt: number }>();

function getCached(spaceId: string): BrandConfig | null | undefined {
  const entry = cache.get(spaceId);
  if (!entry) return undefined;
  if (Date.now() > entry.expiresAt) {
    cache.delete(spaceId);
    return undefined;
  }
  return entry.data;
}

function setCached(spaceId: string, data: BrandConfig | null): void {
  // V4-BRAND-CONFIG-STALE-CACHE (2026-07-03) — ne jamais mettre en cache un état
  // transitoire (pas encore extrait / en cours). L'extraction SetupExpress tourne
  // en arrière-plan (job arq) pendant que l'utilisateur est sur le dashboard, sans
  // aucun moyen d'invalider ce cache mémoire côté front. Si un mount antérieur
  // (ex: fetch du dashboard avant la fin de l'extraction) a caché `null`/`pending`,
  // rouvrir l'onglet Charte graphique après succès continuait de servir ce résultat
  // périmé pendant 5 min, affichant "Aucune couleur détectée" alors que le backend
  // avait déjà tout extrait. Seuls les états terminaux (succeeded/failed) sont
  // stables et peuvent être mis en cache sans risque.
  if (data === null || data.extraction_status === 'pending' || data.extraction_status == null) {
    cache.delete(spaceId);
    return;
  }
  cache.set(spaceId, { data, expiresAt: Date.now() + CACHE_TTL_MS });
}

function invalidate(spaceId: string): void {
  cache.delete(spaceId);
}

export const brandConfigService = {
  /**
   * Déclenche l'extraction de la charte depuis ``landing_page_url``.
   *
   * Si ``landingPageUrl`` n'est pas fourni, le backend utilise
   * ``space.landing_page_url`` déjà persisté. Retourne 202 immédiatement
   * (job arq enqueué) — le caller doit ensuite poll {@link get} jusqu'à
   * obtenir ``extraction_status === 'succeeded' | 'failed'``.
   */
  extract: async (spaceId: string, landingPageUrl?: string): Promise<{ status: string }> => {
    Logger.info('[BrandConfigService] Triggering extraction', { spaceId, hasUrl: !!landingPageUrl });
    invalidate(spaceId); // l'extraction va changer la config → invalide le cache
    const response = await apiClient.post<{ status: string }>(
      `/space/${spaceId}/brand-config/extract`,
      { landing_page_url: landingPageUrl ?? null },
    );
    return response.data;
  },

  /**
   * Récupère la charte actuelle du space. Renvoie `null` si jamais extraite.
   * Pendant l'extraction, retourne un BrandConfig avec ``extraction_status === 'pending'``.
   *
   * @param spaceId
   * @param options.forceFresh - si true, bypass le cache (utile pour le polling pendant extraction).
   */
  get: async (
    spaceId: string,
    options: { forceFresh?: boolean } = {},
  ): Promise<BrandConfig | null> => {
    if (!options.forceFresh) {
      const cached = getCached(spaceId);
      if (cached !== undefined) {
        Logger.debug('[BrandConfigService] cache hit', { spaceId });
        return cached;
      }
    }
    Logger.debug('[BrandConfigService] Fetching brand config', { spaceId });
    const response = await apiClient.get<BrandConfig | null>(`/space/${spaceId}/brand-config`);
    const data = response.data ?? null;
    setCached(spaceId, data);
    return data;
  },

  /**
   * Met à jour manuellement la charte (édition utilisateur en Settings).
   *
   * Le statut est forcé à `succeeded` côté backend si le caller envoie `pending`.
   */
  update: async (spaceId: string, config: BrandConfig): Promise<BrandConfig> => {
    Logger.info('[BrandConfigService] Updating brand config', { spaceId });
    const response = await apiClient.put<BrandConfig>(`/space/${spaceId}/brand-config`, config);
    setCached(spaceId, response.data); // le PUT renvoie la version à jour → on update le cache
    return response.data;
  },

  /**
   * V4-BRAND-01 2026-05-29 — Réinitialise le statut d'extraction (utilisé
   * quand `extraction_status === 'failed'` : permet de re-déclencher
   * l'extraction proprement depuis l'UI au lieu d'attendre un TTL côté
   * backend ou un changement d'URL).
   *
   * Le backend (autre agent) expose `POST /space/{spaceId}/brand-config/reset`
   * qui remet `extraction_status = null` et clear l'erreur.
   */
  reset: async (spaceId: string): Promise<{ status: string }> => {
    Logger.info('[BrandConfigService] Resetting extraction state', { spaceId });
    invalidate(spaceId);
    const response = await apiClient.post<{ status: string }>(
      `/space/${spaceId}/brand-config/reset`,
    );
    return response.data;
  },

  /**
   * Invalide manuellement le cache pour un space (ex: après une action qui change la config).
   */
  invalidateCache: invalidate,
};
